import React, { useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, CircularProgress } from '@mui/material';
import * as XLSX from 'xlsx';
import { bulkImportLeads } from '../services/leadService';

const desiredOrder = [
  'first_name','last_name','company_name','lead_status','email','phone_number',
  'gst_number','contact_name','follow_up_date','priority','assigned_salesperson',
  'hotness','amount','notes','created_by',
  'shipping_address','shipping_landmark','shipping_city','shipping_state','shipping_pincode',
  'billing_address','billing_landmark','billing_city','billing_state','billing_pincode',
  'source','event_type','event_date','event_time','event_start_date','event_start_time','event_end_date','event_end_time',
  'event_location','pax','product_id','product_name'
];

const BulkImportModal = ({ open, onClose, onComplete }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const downloadTemplate = (type = 'xlsx') => {
    const ws = XLSX.utils.aoa_to_sheet([desiredOrder]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contacts');

    if (type === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contacts_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } else {
      XLSX.writeFile(wb, 'contacts_template.xlsx');
    }
  };

  const downloadErrorsCsv = (errs = []) => {
    if (!errs || !errs.length) return;
    const rows = [['row', 'error'], ...errs.map((r) => [r.row, r.error])];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_import_errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const result = await bulkImportLeads(file);
      onComplete?.(null, result);

      if (result?.failed > 0) {
        setErrors(result.errors || []);
        // keep modal open so user can review/download errors
      } else {
        // success: clear and close
        setFile(null);
        setErrors([]);
        onClose();
      }
    } catch (err) {
      onComplete?.(err);
      setErrors([{ row: '-', error: err.message || 'Upload failed' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Bulk Import Contacts</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', mt: 1 }}>
          <div>Download a template (XLSX or CSV), fill rows, then upload the file.</div>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={() => downloadTemplate('xlsx')}>Download XLSX Template</Button>
            <Button variant="outlined" onClick={() => downloadTemplate('csv')}>Download CSV Template</Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
            <input ref={fileInputRef} style={{ display: 'none' }} type="file" accept=".xlsx,.csv" id="bulk-import-file" onChange={handleFileChange} />
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>Choose file</Button>
            <div>{file ? file.name : 'No file chosen'}</div>
          </Box>

          <Box sx={{ color: 'text.secondary', fontSize: 13, mt: 2 }}>
            Note: Column names should match the template headers. The backend will ignore unknown columns.
          </Box>

          {errors?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Import errors ({errors.length})</div>
              <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee', padding: 8, borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>Row</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((err, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa' }}>{err.row}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa' }}>{err.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" onClick={() => downloadErrorsCsv(errors)}>Download errors CSV</Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleUpload} disabled={!file || loading} variant="contained">
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportModal;
